import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { submitContact, submitConsultation, submitServiceRequest } from "../lib/api";

const inputCls =
  "w-full bg-white/5 border border-[#C0C0C0]/50 rounded-xl px-4 py-3 text-[#EFE7FA] font-light placeholder:text-[#EFE7FA]/40 focus:outline-none focus:border-[#5B2C83] focus:ring-2 focus:ring-[#5B2C83]/10 transition-all";
const labelCls = "block text-sm text-[#EFE7FA]/70 mb-2 tracking-wide";

function SubmitButton({ loading, label, testid }) {
  return (
    <button type="submit" disabled={loading} data-testid={testid} className="btn-primary w-full mt-2 disabled:opacity-60">
      {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={17} />}
      {loading ? "Sending..." : label}
    </button>
  );
}

export function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message || "Message sent.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Full name</label>
          <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid="contact-name" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="contact-email" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Subject</label>
        <input value={form.subject} onChange={set("subject")} className={inputCls} placeholder="How can we help?" data-testid="contact-subject" />
      </div>
      <div>
        <label className={labelCls}>Message</label>
        <textarea required rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="Share what's on your heart..." data-testid="contact-message" />
      </div>
      <SubmitButton loading={loading} label="Send Message" testid="contact-submit" />
    </form>
  );
}

export function ConsultationForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", focus: "Grow", preferred_date: "", preferred_time: "Morning (9am–12pm)", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitConsultation(form);
      toast.success(res.message || "Consultation request received.");
      setForm({ name: "", email: "", phone: "", focus: "Grow", preferred_date: "", preferred_time: "Morning (9am–12pm)", message: "" });
    } catch {
      toast.error("Could not book your consultation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="consultation-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Full name</label>
          <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid="consultation-name" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="consultation-email" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Phone (optional)</label>
          <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(000) 000-0000" data-testid="consultation-phone" />
        </div>
        <div>
          <label className={labelCls}>Area of focus</label>
          <select value={form.focus} onChange={set("focus")} className={inputCls} data-testid="consultation-focus">
            <option>Grow — Education & Wellness</option>
            <option>Build — Business & Strategy</option>
            <option>Thrive — Coaching & Leadership</option>
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Preferred date</label>
          <input required type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="consultation-date" />
        </div>
        <div>
          <label className={labelCls}>Preferred time</label>
          <select value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} data-testid="consultation-time">
            <option>Morning (9am–12pm)</option>
            <option>Afternoon (12pm–4pm)</option>
            <option>Evening (4pm–7pm)</option>
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>What would you like to explore?</label>
        <textarea rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Tell us a little about your goals..." data-testid="consultation-message" />
      </div>
      <SubmitButton loading={loading} label="Request Consultation" testid="consultation-submit" />
    </form>
  );
}

export function ServiceRequestForm() {
  const [form, setForm] = useState({ name: "", email: "", service: "Business consulting", budget: "", details: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitServiceRequest(form);
      toast.success(res.message || "Service request received.");
      setForm({ name: "", email: "", service: "Business consulting", budget: "", details: "" });
    } catch {
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="service-request-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Full name</label>
          <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid="service-name" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="service-email" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Service</label>
          <select value={form.service} onChange={set("service")} className={inputCls} data-testid="service-type">
            <option>Business consulting</option>
            <option>AI automation</option>
            <option>Systems & strategy</option>
            <option>Coaching</option>
            <option>Leadership development</option>
            <option>Family wellness</option>
            <option>Personal development</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Budget (optional)</label>
          <input value={form.budget} onChange={set("budget")} className={inputCls} placeholder="e.g. $1,000–$5,000" data-testid="service-budget" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Project details</label>
        <textarea required rows={5} value={form.details} onChange={set("details")} className={inputCls} placeholder="Describe what you're looking for..." data-testid="service-details" />
      </div>
      <SubmitButton loading={loading} label="Submit Request" testid="service-submit" />
    </form>
  );
}
